import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Filler,
    Tooltip,
    Legend,
    ChartOptions,
    ScriptableContext,
    Chart, } from "chart.js";
import { Line } from "react-chartjs-2";

import "./styles.scss";
import { UiCheckboxSwitch, UiIcon } from 'shared/uikit';
import { Portal } from 'react-portal';
import { observer } from 'mobx-react';
import { UserDashboardWidgetModel } from 'shared/models';
import { ICONS } from 'shared/constants';
import { useDebounce, useMedia } from 'shared/hooks';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

type  PropsType = {
    widget: UserDashboardWidgetModel
}

const getProgressPercent = (value: number, total: number) => {
    if (!Number.isFinite(value) || !Number.isFinite(total) || total <= 0) {
        return 0;
    }

    return Math.min((value / total) * 100, 100);
};

const formatTooltipDate = (date?: string) => {
    const [, year, month, day] = date?.match(/^(\d{4})-(\d{2})-(\d{2})$/) ?? [];

    if (day && month && year) {
        return `${day}.${month}.${year}`;
    }

    return '';
};

export const PPDWidgetLineChart = observer (({ widget }: PropsType) => {
    const chartRef = useRef<Chart<"line"> | null>(null);
    const scrollRef = useRef<HTMLDivElement>(null);
    const { isMobileOrTablet } = useMedia()

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;

        let isDown = false;
        let isTouchScrolling = false;
        let startX = 0;
        let scrollLeft = 0;

        const SCROLL_THRESHOLD = 10;

        const startDrag = (e: MouseEvent | TouchEvent) => {
            isDown = true;
            isTouchScrolling = false;

            startX = "touches" in e
                ? e.touches[0].pageX - el.offsetLeft
                : (e as MouseEvent).pageX - el.offsetLeft;

            scrollLeft = el.scrollLeft;
        };

        const moveDrag = (e: MouseEvent | TouchEvent) => {
            if (!isDown) return;

            const x = "touches" in e
                ? e.touches[0].pageX - el.offsetLeft
                : (e as MouseEvent).pageX - el.offsetLeft;

            const offset = x - startX;

            if (Math.abs(offset) > SCROLL_THRESHOLD) {
                isTouchScrolling = true;
                e.preventDefault();
                el.scrollLeft = scrollLeft - offset;
            }
        };

        const stopDrag = () => {
            isDown = false;
            if (!isTouchScrolling) {
                chartRef.current?.update("none");
            }
        };

        el.addEventListener("mousedown", startDrag);
        el.addEventListener("touchstart", startDrag, { passive: false });
        el.addEventListener("mousemove", moveDrag);
        el.addEventListener("touchmove", moveDrag, { passive: false });
        el.addEventListener("mouseup", stopDrag);
        el.addEventListener("mouseleave", stopDrag);
        el.addEventListener("touchend", stopDrag);

        return () => {
            el.removeEventListener("mousedown", startDrag);
            el.removeEventListener("touchstart", startDrag);
            el.removeEventListener("mousemove", moveDrag);
            el.removeEventListener("touchmove", moveDrag);
            el.removeEventListener("mouseup", stopDrag);
            el.removeEventListener("mouseleave", stopDrag);
            el.removeEventListener("touchend", stopDrag);
        };
    }, []);

    const hideTooltipsAndDrawChart = () => {
        setTooltipData(null);
        setDailyDiffTooltip(null);
        crosshairRef.current = null;
        axisXRef.current = null;

        const chart = chartRef.current;
        if (!chart) return;

        try {
            chart.update('none');
        } catch (e) {
            console.log(e)
        }
    };

    useEffect(() => {
        const scrollEl = scrollRef.current;
        if (!scrollEl) return;

        scrollEl.addEventListener('scroll', hideTooltipsAndDrawChart, { passive: true });

        return () => {
            scrollEl.removeEventListener('scroll', hideTooltipsAndDrawChart);
        };
    }, []);

    const debouncedUpdateChart = useDebounce(() => {
        const chart = chartRef.current;
        if (chart) {
            chart.update('none');
        }
    }, 150);

    useEffect(() => {
        const scrollEl = scrollRef.current;
        if (!scrollEl) return;

        const handleScroll = () => {
            debouncedUpdateChart();
        };

        scrollEl.addEventListener('scroll', handleScroll, { passive: true });
        return () => {
            scrollEl.removeEventListener('scroll', handleScroll);
        };
    }, [debouncedUpdateChart])

    useEffect(() => {
        const chart = chartRef.current;
        if (!chart) return;

        const ro = new ResizeObserver(() => {
            hideTooltipsAndDrawChart();
            chart.resize();
            chart.update('none');
        });

        ro.observe(chart.canvas.parentElement!);
        return () => ro.disconnect();
    }, []);

    const crosshairRef = useRef<null | { x: number; y: number; index: number }>(null);
    const axisXRef = useRef<null | { x: number }>(null);

    const chartItems = widget.items[0];
    const currentMonthItems = chartItems?.current_month ?? [];
    const previousMonthItems = chartItems?.previous_month ?? [];
    const sourceDays = Math.max(
        currentMonthItems.length,
        previousMonthItems.length
    );
    const hasChartData = sourceDays > 0;
    const totalDays = sourceDays || widget.workdays || 0;
    const days = useMemo(() => Array.from({ length: totalDays }, (_, i) => i + 1), [totalDays]);
    const currentTooltipDates = useMemo(() =>
        days.map((_, i) => formatTooltipDate(currentMonthItems[i]?.date)),
    [days, currentMonthItems]
    );
    const previousTooltipDates = useMemo(() =>
        days.map((_, i) => formatTooltipDate(previousMonthItems[i]?.date)),
    [days, previousMonthItems]
    );
    const currentMonth: (number | null)[] = useMemo(() =>
        days.map((_, i) => currentMonthItems[i]?.value ?? null),
    [days, currentMonthItems]
    );

    const previousMonth: (number | null)[] = useMemo(() =>
        days.map((_, i) => previousMonthItems[i]?.value ?? null), [days, previousMonthItems]
    );

    const dailyPlan = widget.workdays > 0 ? widget.plan.amount / widget.workdays : 0;
    const currentDay = currentMonthItems.length;

    const currentNormAmount = Math.round(Math.min(
        dailyPlan * currentDay,
        widget.plan.amount
    ));

    const currentNormPercent = getProgressPercent(currentNormAmount, widget.plan.amount);

    const factAmount = widget.fact.amount;
    const factPercent = getProgressPercent(factAmount, widget.plan.amount);

    const planMet = useMemo(
        () => currentMonthItems.map((d: any) => d.value >= dailyPlan),
        [currentMonthItems, dailyPlan]
    );

    const chartWidth = Math.max(768, days.length * 30);

    const [showPrevious, setShowPrevious] = useState(true);
    const [isScrollable, setIsScrollable] = useState(false);
    const [yTicks, setYTicks] = useState<{ value: number; y: number }[]>([]);
    const [tooltipData, setTooltipData] = useState<{
        x: number;
        y: number;
        index: number;
        curr: number | null;
        prev?: number | null;
        currentDate: string;
        previousDate: string;
        chartRect?: DOMRect;
    } | null>(null);

    const [dailyDiffTooltip, setDailyDiffTooltip] = useState<{
        day: number;
        x: number;
        plan: number;
        fact: number | null;
        diff: number;
        chartRect?: DOMRect;
    } | null>(null);

    const safeCurr = currentMonth.filter((v): v is number => v != null);
    const safePrev = previousMonth.filter((v): v is number => v != null);

    const maxValue = Math.max(...safeCurr, ...safePrev, dailyPlan, 0);

    const maxY = Math.ceil(maxValue / 50000) * 50000 || 50000;
    const stepY = maxY / 5;

    const crosshairPlugin = useMemo(
        () => ({
            id: "crosshairPlugin",
            afterDatasetsDraw: (chart: any) => {
                const ch = crosshairRef.current;
                if (!ch) return;

                const { ctx } = chart;
                const x = ch.x;
                const topY = chart.chartArea.top;
                const bottomY = chart.chartArea.bottom;

                ctx.save();
                ctx.beginPath();
                ctx.setLineDash([3, 3]);
                ctx.moveTo(x + 0.5, topY);
                ctx.lineTo(x + 0.5, bottomY);
                ctx.lineWidth = 1;
                ctx.strokeStyle = "#15171C";
                ctx.stroke();
                ctx.restore();

                const activeIndex = ch.index;
                chart.data.datasets.forEach((ds: any, i: number) => {
                    const meta = chart.getDatasetMeta(i);
                    if (!meta?.data?.[activeIndex]) return;
                    const point = meta.data[activeIndex];
                    const cx = point.x;
                    const cy = point.y;

                    ctx.save();
                    ctx.globalCompositeOperation = "source-over";
                    ctx.beginPath();
                    ctx.arc(cx, cy, 4.5, 0, Math.PI * 2);
                    ctx.fillStyle = "#FFFFFF";
                    ctx.strokeStyle = "#15171C";
                    ctx.lineWidth = 2;
                    ctx.fill();
                    ctx.stroke();
                    ctx.restore();
                });
            },
        }),
        []
    );

    const axisXPlugin = useMemo(
        () => ({
            id: "axisXPlugin",
            afterDatasetsDraw: (chart: any) => {
                const pos = axisXRef.current;
                if (!pos) return;

                const { ctx } = chart;
                const bottomY = chart.chartArea.bottom;

                ctx.save();
                ctx.globalCompositeOperation = "source-over";
                ctx.beginPath();
                ctx.arc(pos.x, bottomY, 4.5, 0, Math.PI * 2);
                ctx.fillStyle = "#FFFFFF";
                ctx.strokeStyle = "#15171C";
                ctx.lineWidth = 2;
                ctx.fill();
                ctx.stroke();
                ctx.restore();
            },
        }),
        []
    );

    const differenceBlendPlugin = useMemo(() => ({
        id: "differenceBlendPlugin",

        beforeDatasetDraw(chart: any, args: any) {
            const ctx = chart.ctx;

            const secondDatasetIndex = 1;

            if (args.index === secondDatasetIndex) {
                ctx.save();
                ctx.globalCompositeOperation = "multiply";
            }
        },
    }), []);

    const createPlanBasedGradient = (chart: Chart<"line">, dailyPlan: number)  => {
        const { ctx, chartArea, scales } = chart as any;

        if (!chartArea || !scales?.y) {
            return "rgba(22,163,74,0.8)";
        }

        const gradient = ctx.createLinearGradient(
            0,
            chartArea.top,
            0,
            chartArea.bottom
        );

        const yScale = scales.y;

        const top = chartArea.top;
        const bottom = chartArea.bottom;
        const height = bottom - top;

        const planY = yScale.getPixelForValue(dailyPlan);
        const planStop = Math.min(1, Math.max(0, (planY - top) / height));

        const clamp = (v: number) => Math.min(1, Math.max(0, v));
        gradient.addColorStop(0, "rgba(22,163,74,0.8)");

        gradient.addColorStop(
            clamp(planStop),
            "rgba(22,163,74,0.8)"
        );

        gradient.addColorStop(
            clamp(planStop + (1 - planStop) * 0.35),
            "rgba(152,209,60,0.8)"
        );

        gradient.addColorStop(
            clamp(planStop + (1 - planStop) * 0.7),
            "rgba(251,146,60,0.8)"
        );

        gradient.addColorStop(1, "rgba(225,29,72,0.8)");

        return gradient;
    }

    const datasets = useMemo(
        () => [{
            label: widget.labels.current,
            data: currentMonth,
            fill: true,
            backgroundColor: (context: ScriptableContext<"line">) => {
                return createPlanBasedGradient(
                    context.chart as Chart<"line">,
                    dailyPlan
                );
            },
            borderColor: "transparent",
            borderWidth: 0,
            tension: 0.3,
            pointRadius: 0,
        }, showPrevious
            ? {
                label: widget.labels.previous,
                data: previousMonth,
                fill: true,
                backgroundColor: "rgba(120, 123, 131, 0.2)",
                borderColor: "transparent",
                borderWidth: 0,
                tension: 0.3,
                pointRadius: 0,
            }
            : null,].filter(Boolean),
        [currentMonth, previousMonth, showPrevious, dailyPlan]
    );

    const data = useMemo(() => ({ labels: days, datasets: datasets as any[] }), [days, datasets]);

    const options: ChartOptions<"line"> = useMemo(
        () => ({
            responsive: true,
            maintainAspectRatio: false,
            interaction: { mode: "index", intersect: false },
            events: ["mousemove", "mouseout", "click", "touchstart", "touchmove", "touchend"],
            elements: {
                point: {
                    radius: 0,
                    hoverRadius: 0,
                    hitRadius: 0,
                }
            },
            plugins: {
                legend: { display: false },
                tooltip: {
                    enabled: false,
                    external: (ctx: any) => {
                        const { chart, tooltip } = ctx;

                        if (!tooltip || tooltip.opacity === 0) {
                            setTooltipData(null);
                            crosshairRef.current = null;
                            axisXRef.current = null;
                            chart.draw();
                            return;
                        }

                        const mouseY = tooltip.y ?? 0;
                        const axisZoneTop = chart.chartArea.bottom - 30;

                        if (mouseY > axisZoneTop) {
                            setTooltipData(null);
                            crosshairRef.current = null;
                            chart.draw();
                            return;
                        }

                        const dp = tooltip.dataPoints?.[0];
                        if (!dp) return;

                        const index = dp.dataIndex;
                        const curr = currentMonth[index];
                        const prev = previousMonth[index];
                        const x = dp.element.x;
                        const y = dp.element.y;

                        setDailyDiffTooltip(null);
                        axisXRef.current = null;

                        crosshairRef.current = { x, y, index };
                        setTooltipData({
                            x,
                            y,
                            index,
                            curr,
                            prev,
                            currentDate: currentTooltipDates[index],
                            previousDate: previousTooltipDates[index],
                            chartRect: chart.canvas.getBoundingClientRect(),
                        });
                        chart.draw();
                    },
                },
            },
            scales: {
                x: {
                    type: "category",
                    offset: false,
                    grid: { display: false, drawBorder: false },
                    ticks: {
                        align: "center",
                        padding: 10,
                        font: {
                            size: isMobileOrTablet ? 11 : 12,
                            family: "'PF Bague Sans Pro', sans-serif"
                        },
                        color: (context: any) => {
                            const day = Number(context.tick.label);
                            if (day > currentDay) return "#9EA2AA";
                            if (day === currentDay) return "#2C2F35";
                            const result = dailyResults.find((r: any) => r.day === day);
                            if (!result) return "#9EA2AA";
                            return result.done ? "#22C55E" : "#F43F5E";
                        },
                        callback: () => '',
                    },
                },
                y: {
                    display: true,
                    grid: {
                        color: "#EAECF1",
                        drawBorder: false,
                        drawTicks: false,
                        borderWidth: 0,
                        lineWidth: 1,
                    },
                    ticks: {
                        display: false,
                        stepSize: stepY
                    },
                    min: 0,
                    max: maxY,
                },
            },
            layout: { padding: { left: 0, right: 0, top: 10, bottom: 10 } },
            animation: false,
        }),
        [maxY, stepY, planMet, currentMonth, previousMonth, currentTooltipDates, previousTooltipDates]
    );

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        const check = () => setIsScrollable(el.scrollWidth > el.clientWidth + 10);
        check();
        const ro = new ResizeObserver(check);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    const handleTogglePrevious = ({ value }: { value: number }) => setShowPrevious(Boolean(value));

    const relevantDays = currentMonthItems.slice(0, currentDay - 1);

    const dailyResults = relevantDays.map((d: any, i: number) => ({
        day: i + 1,
        done: d.value >= dailyPlan
    }));

    const xAxisMarkersPlugin = useMemo(() => ({
        id: "xAxisMarkersPlugin",
        afterDraw: (chart: any) => {
            const xScale = chart.scales.x;
            if (!xScale) return;

            const topY = chart.chartArea.bottom + 14;
            const bottomY = chart.chartArea.bottom + 17;
            const { ctx } = chart;

            ctx.save();
            ctx.lineWidth = 2;
            ctx.lineCap = "round";
            days.forEach((day, index) => {
                const result = dailyResults.find((r: any) => r.day === day);
                const color = day > currentDay
                    ? "#9EA2AA"
                    : day === currentDay
                        ? "#2C2F35"
                        : result?.done
                            ? "#22C55E"
                            : "#F43F5E";
                const x = xScale.getPixelForValue(index);

                ctx.beginPath();
                ctx.moveTo(x, topY);
                ctx.lineTo(x, bottomY);
                ctx.strokeStyle = color;
                ctx.stroke();
            });
            ctx.restore();
        },
    }), [currentDay, dailyResults, days]);

    function countRecentFails(days: { done: boolean }[]): number {
        let streak = 0;
        for (let i = days.length - 1; i >= 0; i--) {
            if (!days[i].done) streak++;
            else break;
        }
        return streak;
    }

    const recentFailStreak = countRecentFails(dailyResults);

    function pluralizeDays(n: number): string {
        const abs = Math.abs(n) % 100;
        const last = abs % 10;
        if (abs > 10 && abs < 20) return "дней";
        if (last > 1 && last < 5) return "дня";
        if (last === 1) return "день";
        return "дней";
    }

    let statusLabel = "Выполнение ежедневного плана в норме";
    let statusType: "normal" | "warning" | "alert" = "normal";

    if (recentFailStreak >= 3) {
        statusLabel = `Невыполнение плана ${recentFailStreak} ${pluralizeDays(recentFailStreak)} подряд`;
        statusType = "alert";
    } else if (recentFailStreak >= 1) {
        statusLabel = 'Ежедневный план не выполнен';
        statusType = "warning";
    }

    function formatAxisValue(value: number): string {
        if (Math.abs(value) >= 1000) {
            return `${(value / 1000).toFixed(value % 1000 === 0 ? 0 : 1)}к`;
        }
        return value.toString();
    }

    useEffect(() => {
        const chart = chartRef.current;
        if (!chart) return;

        const updateTicks = () => {
            const yScale = chart.scales.y;
            if (!yScale) return;

            const topOffset = 6

            const ticks = [];
            for (let v = 0; v <= maxY; v += stepY) {
                const y = yScale.getPixelForValue(v) - topOffset;
                ticks.push({ value: v, y });
            }
            setYTicks(ticks);
        };

        updateTicks();
        const ro = new ResizeObserver(updateTicks);
        ro.observe(chart.canvas);
        return () => ro.disconnect();
    }, [maxY, stepY, data]);


    const tooltipRef = useRef<HTMLDivElement>(null);
    const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>();
    const [tooltipSide, setTooltipSide] = useState<"left" | "right" | "center">("center");

    useLayoutEffect(() => {
        if (tooltipData?.chartRect && tooltipRef.current) {
            const tooltipWidth = tooltipRef.current.offsetWidth;
            const { chartRect, x, y } = tooltipData;

            const defaultLeft = chartRect.left + x + window.scrollX;
            let left = defaultLeft - tooltipWidth / 2;

            const viewportRight = window.scrollX + window.innerWidth;
            const viewportLeft = window.scrollX;

            let side: "left" | "right" | "center" = "center";

            if (left + tooltipWidth > viewportRight - 8) {
                left = defaultLeft - tooltipWidth - 8;
                side = "left";
            } else if (left < viewportLeft + 8) {
                left = defaultLeft + 8;
                side = "right";
            }

            setTooltipSide(side);

            const topOffset = side === "center" ? 60 : 30;

            setTooltipStyle({
                left,
                top: chartRect.top + y - topOffset + window.scrollY,
            });
        }
    }, [tooltipData, tooltipSide]);

    const tooltipPortal = tooltipData ? (
        <Portal>
            <div
                ref={tooltipRef}
                className={`ppd-widget-line-chart__tooltip ppd-widget-line-chart__tooltip--${tooltipSide}`}
                style={tooltipStyle}
            >
                {showPrevious ? (
                    <>
                        {tooltipData.curr != null && (
                            <div className="ppd-widget-line-chart__tooltip-row">
                                <span className="ppd-widget-line-chart__tooltip-label">{tooltipData.currentDate}</span>
                                <span className="ppd-widget-line-chart__tooltip-value">
                                    {tooltipData.curr.toLocaleString("ru-RU")} ₽
                                </span>
                            </div>
                        )}
                        {tooltipData.prev != null && (
                            <div className="ppd-widget-line-chart__tooltip-row">
                                <span className="ppd-widget-line-chart__tooltip-label">{tooltipData.previousDate}</span>
                                <span className="ppd-widget-line-chart__tooltip-value">
                                    {tooltipData.prev.toLocaleString("ru-RU")} ₽
                                </span>
                            </div>
                        )}
                    </>
                ) : (
                    tooltipData.curr != null && (
                        <div className="ppd-widget-line-chart__tooltip-row">
                            <span className="ppd-widget-line-chart__tooltip-label">{tooltipData.currentDate}</span>
                            <span className="ppd-widget-line-chart__tooltip-value">
                                {tooltipData.curr.toLocaleString("ru-RU")} ₽
                            </span>
                        </div>
                    )
                )}

                <span className="ppd-widget-line-chart__tooltip-arrow" />
            </div>
        </Portal>
    ) : null;

    const dailyTooltipOffset =  87

    const dailyDiffTooltipPortal =
        dailyDiffTooltip && dailyDiffTooltip.chartRect ? (
            <Portal>
                <div
                    className="ppd-widget-line-chart__tooltip-diff"
                    style={{
                        left: dailyDiffTooltip.chartRect.left + dailyDiffTooltip.x + window.scrollX,
                        top: dailyDiffTooltip.chartRect.bottom - dailyTooltipOffset + window.scrollY,
                    }}
                >
                    <span className="ppd-widget-line-chart__tooltip-diff-value">
                        {Math.round(Math.abs(dailyDiffTooltip.diff)).toLocaleString("ru-RU")} ₽
                    </span>
                    {
                        dailyDiffTooltip.diff > 0 && <UiIcon icon={ICONS.GROWTH} size={16} className="ppd-widget-line-chart__tooltip-diff-icon"/>
                    }
                    {
                        dailyDiffTooltip.diff < 0 && <UiIcon icon={ICONS.FALL} size={16} className="ppd-widget-line-chart__tooltip-diff-icon"/>
                    }
                    <span className="ppd-widget-line-chart__tooltip-diff-arrow"></span>
                </div>
            </Portal>
        ) : null;

    useEffect(() => {
        const chart = chartRef.current;
        if (!chart) return;

        const canvas = chart.canvas;

        const handleMove = (e: MouseEvent) => {
            const rect = canvas.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const y = e.clientY - rect.top;

            const xScale = chart.scales.x;
            const dayIndex = xScale.getValueForPixel(x);
            const rounded = Math.round(dayIndex || 0);

            const isOnAxisX = y > chart.chartArea.bottom + 10 && y < chart.chartArea.bottom + 40;

            if (isOnAxisX && rounded >= 0 && rounded < days.length) {
                const day = days[rounded];

                if (day > currentDay) {
                    setDailyDiffTooltip(null);
                    axisXRef.current = null;
                    chart.draw();
                    return;
                }

                setTooltipData(null);
                crosshairRef.current = null;

                const fact = currentMonthItems[day - 1]?.value ?? null;
                const plan = dailyPlan;
                const diff = fact != null ? fact - plan : 0;
                const xPos = xScale.getPixelForValue(day - 1);

                axisXRef.current = { x: xPos };

                setDailyDiffTooltip({
                    day,
                    x: xPos,
                    plan,
                    fact,
                    diff,
                    chartRect: rect,
                });

                chart.draw();
                return;
            }

            setDailyDiffTooltip(null);
            axisXRef.current = null;
        };

        const handleLeave = () => {
            setDailyDiffTooltip(null);
            axisXRef.current = null;
            crosshairRef.current = null;
            setTooltipData(null);
            chart.draw();
        };

        canvas.addEventListener("mousemove", handleMove);
        canvas.addEventListener("mouseleave", handleLeave);

        return () => {
            canvas.removeEventListener("mousemove", handleMove);
            canvas.removeEventListener("mouseleave", handleLeave);
        };
    }, [days, currentMonthItems, dailyPlan]);

    return (
        <div className="ppd-widget-line-chart">
            <div className="ppd-widget-line-chart__header">
                <div className="ppd-widget-line-chart__header-top">
                    <h1 className="ppd-widget-line-chart__title">{widget.name}</h1>
                    {hasChartData && (
                        <UiCheckboxSwitch
                            label="Показывать прошлый месяц"
                            value={showPrevious ? 1 : 0}
                            name="show_previous"
                            size={'large'}
                            theme={'dark'}
                            onChange={handleTogglePrevious}
                        />
                    )}
                </div>
                {hasChartData && (
                    <div className="ppd-widget-line-chart__indicators">
                        {
                            widget.fact.amount - currentNormAmount < 0 && (
                                <div className="ppd-widget-line-chart__indicator ppd-widget-line-chart__indicator--negative">
                                    <span className="ppd-widget-line-chart__indicator-label">Отставание</span>
                                    <span className="ppd-widget-line-chart__indicator-value">
                                        {(widget.fact.amount - currentNormAmount).toLocaleString("ru-RU")} ₽
                                    </span>
                                </div>
                            )
                        }
                        <div
                            className={`ppd-widget-line-chart__indicator ${
                                statusType === "alert"
                                    ? "ppd-widget-line-chart__indicator--alert"
                                    : statusType === "warning"
                                        ? "ppd-widget-line-chart__indicator--warning"
                                        : "ppd-widget-line-chart__indicator--normal"
                            }`}
                        >
                            {
                                statusType === "alert" && (
                                    <UiIcon icon={ICONS.INFO_CIRCLE} size={18} color="#FFFFFF" className="ppd-widget-line-chart__alert-icon" />
                                )
                            }
                            <span className="ppd-widget-line-chart__indicator-text">{statusLabel}</span>
                        </div>
                    </div>
                )}
            </div>

            <div className="ppd-widget-line-chart__body">
                <div className="ppd-widget-line-chart__y-axis">
                    {yTicks.map((t, i) => (
                        <div
                            className="ppd-widget-line-chart__y-label"
                            key={i}
                            style={{
                                top: `${t.y}px`,
                            }}
                        >
                            {formatAxisValue(t.value)}
                        </div>
                    ))}
                </div>
                <div
                    className="ppd-widget-line-chart__scroll"
                    ref={scrollRef}
                >
                    <div
                        className="ppd-widget-line-chart__canvas-wrapper"
                        style={{
                            width: `${chartWidth}px`,
                        }}
                    >
                        <Line ref={chartRef} data={data} options={options} plugins={[differenceBlendPlugin, crosshairPlugin, axisXPlugin, xAxisMarkersPlugin]} />
                        {tooltipPortal}
                        {dailyDiffTooltipPortal}
                    </div>
                </div>
            </div>

            {isScrollable && (
                <div className="ppd-widget-line-chart__scroll-hint">
                    <UiIcon icon={ICONS.CHART_ARROW_LEFT} color={"#787B83"} size={18}/>
                    пролистайте график
                    <UiIcon icon={ICONS.CHART_ARROW_RIGHT} color={"#787B83"} size={18}/>
                </div>
            )}
            <div className="ppd-widget-line-chart__legend">
                <div className="ppd-widget-line-chart__legend-item">
                    <div className="ppd-widget-line-chart__legend-color-wrapper">
                        <div className="ppd-widget-line-chart__legend-color ppd-widget-line-chart__legend-color--green"
                            style={{ width: `${currentNormPercent}%` }}
                        ></div>
                    </div>
                    <div className="ppd-widget-line-chart__legend-text">
                        <p className="ppd-widget-line-chart__legend-label">{widget.labels.plan}</p>
                        <span className="ppd-widget-line-chart__legend-value">
                            {currentNormAmount.toLocaleString("ru-RU")} ₽
                        </span>
                    </div>
                </div>
                <div className="ppd-widget-line-chart__legend-item">
                    <div className="ppd-widget-line-chart__legend-color-wrapper">
                        <div
                            className={`ppd-widget-line-chart__legend-color ${
                                factAmount >= currentNormAmount
                                    ? "ppd-widget-line-chart__legend-color--green"
                                    : "ppd-widget-line-chart__legend-color--red"
                            }`}
                            style={{ width: `${factPercent}%` }}
                        ></div>
                    </div>
                    <div className="ppd-widget-line-chart__legend-text">
                        <span className="ppd-widget-line-chart__legend-label">{widget.labels.fact}</span>
                        <span className="ppd-widget-line-chart__legend-value">
                            {factAmount.toLocaleString("ru-RU")} ₽
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
});
